/* What a contentIQ article's status actually is.

   `stage` is the editorial workflow (Interview → Draft → Review → Published).
   Delivery is a separate axis: a piece can be pushed to the client's site and
   still be mid-workflow, because it gets edited and published THERE. Conflating
   the two made "Draft" mean both "needs attention in contentIQ" and "already on
   the site awaiting publication", which are opposite calls to action.

   So the status is derived here, in one place, and both the badge and the
   library filter read it — otherwise a row can display one status and be
   filtered under another. */

export type ArticleStatusKey = 'interview' | 'draft' | 'review' | 'pushed' | 'published';

export interface StatusInput {
  stage: number;
  /** When it reached the client's site (Astro repo or DNN). Empty = never. */
  pushedAt?: string;
}

/* Published wins: it is the end of the workflow either way. Otherwise delivery
   beats the workflow stage, because once a piece is on the site the next move
   belongs to whoever edits it there — not to contentIQ. */
export function articleStatus(a: StatusInput): ArticleStatusKey {
  if (a.stage >= 3) return 'published';
  if ((a.pushedAt || '').trim()) return 'pushed';
  if (a.stage === 2) return 'review';
  if (a.stage === 1) return 'draft';
  return 'interview';
}

export const ARTICLE_STATUS_LABEL: Record<ArticleStatusKey, string> = {
  interview: 'Interview',
  draft: 'Draft',
  review: 'In review',
  pushed: 'Pushed to site',
  published: 'Published',
};

/** Longer form, for the filter menu and tooltips. */
export const ARTICLE_STATUS_HINT: Record<ArticleStatusKey, string> = {
  interview: 'Interview not written up yet',
  draft: 'Lives only in contentIQ — needs attention',
  review: 'Awaiting Triton review',
  pushed: 'Delivered to the client’s site, to be finished and published there',
  published: 'Published',
};

/* An AI interview the client started and never finished.

   Starting one writes a stage-0 row; completing it moves that same row to stage
   1. So stage 0 on an `ai` row means nobody ever finished — three of these piled
   up on one account in a single morning and were indistinguishable from the one
   interview that WAS completed and needed writing up.

   Only `ai` qualifies. Stage 0 on an in-person request is the normal waiting
   state, not an abandonment.

   The delay exists so an interview somebody is in the middle of right now does
   not vanish from the list under them. */
export const ABANDON_AFTER_HOURS = 24;

export interface AbandonInput { source?: string; stage: number; createdAt?: string }

/* D1 writes datetime('now') as 'YYYY-MM-DD HH:MM:SS' — UTC, but with no zone
   marker, which Date.parse reads as LOCAL time. Unfixed that is a whole-timezone
   error in how old a row looks. */
export function parseDbTime(s: string): number {
  const t = s.trim();
  return Date.parse(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(t) ? `${t.replace(' ', 'T')}Z` : t);
}

export function isAbandonedInterview(a: AbandonInput, now: number = Date.now()): boolean {
  if (a.source !== 'ai' || a.stage !== 0) return false;
  const started = parseDbTime(a.createdAt || '');
  // An unparseable or missing date is not evidence of abandonment. Hiding a row
  // on a date we could not read would lose work silently.
  if (!Number.isFinite(started)) return false;
  return now - started >= ABANDON_AFTER_HOURS * 3_600_000;
}

/* An interview and the piece generated from it are ONE piece of work.

   Saving a generated article already archives its source interview, but the
   archived row still listed as a peer: the same work twice, under two titles
   with two different statuses, and nothing saying they were the same thing.

   So a story that another row was generated FROM is folded into that row. Keyed
   by id and resolved only when BOTH are present — delete the generated piece and
   the interview returns to the list rather than vanishing with it. */
export interface SupersedeInput { id: string; sourceStoryId?: string; archived?: boolean; moreToWrite?: boolean }

/* Folded by the LINK, archived or not. Only the generator's save archived an
   interview; the auto-writer never does, so every auto-written piece left its
   interview listed as "Draft" beside it — 36 of them across 11 clients.

   The one interview NOT folded is a several-job one with a job still
   unwritten (moreToWrite, decided server-side in worker/story-done.ts): folded
   away, the unwritten job had no way back into view. An archived interview is
   finished by definition and always folds. */
export function supersededStoryIds(articles: SupersedeInput[]): Set<string> {
  const byId = new Map(articles.map((a) => [a.id, a]));
  const out = new Set<string>();
  for (const a of articles) {
    const src = a.sourceStoryId ? byId.get(a.sourceStoryId) : undefined;
    // A row claiming itself as its own source must not erase itself.
    if (src && src.id !== a.id && (src.archived || !src.moreToWrite)) out.add(src.id);
  }
  return out;
}

/* Delivery is its own axis, so a pill reads off the DERIVED status rather than
   stage alone. "Pushed to site" is deliberately NOT `accent` like Draft: the two
   demand opposite actions, and looking alike is how a delivered article gets
   chased as if it still needed writing. Shared by contentIQ and Admin so one
   article cannot wear two different colours on two screens. */
export const ARTICLE_STATUS_VARIANT: Record<ArticleStatusKey, 'success' | 'warning' | 'accent' | 'neutral' | 'outline'> = {
  published: 'success',
  pushed: 'success',
  review: 'warning',
  draft: 'accent',
  interview: 'neutral',
};

/** Menu order for the library's status filter. */
export const ARTICLE_STATUS_ORDER: ArticleStatusKey[] = ['interview', 'draft', 'review', 'pushed', 'published'];
